import { Icon } from 'copilot-design-system';
import { compact } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiDownload as DownloadIcon,
  FiEye as PreviewIcon,
} from 'react-icons/fi';
import { useSelector } from 'react-redux';

import {
  FILE_ACCESS_LEVEL,
  FILE_ACTION_DISABLE_TOOLTIP_LABEL,
  FILE_TYPE,
  FILES_ACTION,
  FOLDER_FILE_KEY,
} from 'src/constants';
import { FlagsContext } from 'src/context';
import { useAppDispatch } from 'src/hooks/useStore';
import { FilesActionsRendererProps } from 'src/legacy/components/AgGrid/Renderer/types';
import { BaseActionsMenuButton } from 'src/legacy/components/Dropdowns';
import { FileClientPermissionChangeConfirmationDialog } from 'src/legacy/components/Files/ChannelContent/components/FileClientPermissionChangeConfirmationDialog';
import {
  getExtensionFromFileKey,
  getFileNameFromFileKey,
} from 'src/legacy/components/Files/helpers';
import { updateFolderPermissions } from 'src/store/files/actions';
import { FileResponse, FilesTableActionInput } from 'src/store/files/types';
import { RootState } from 'src/store/reduxTypes';
import { FileUtils } from 'src/utils';

const getFilesInFolder = (fileData: any, files: Array<FileResponse>) =>
  FileUtils.getAllFilesFromPath({
    files,
    sourcePath: fileData.filePath.join('/'),
    fileType: fileData.type,
    fileKey: '',
    ignoreLinks: true,
  }).filter((f) => f.fields.fileKey !== FOLDER_FILE_KEY);

/**
 * This function handle saving new custom field value
 * @param data row data for this table row, used to determine if this is a file or a folder
 * @param files from the redux state, a list of all files
 * @param filesTableAction function to trigger a download
 */
const getDownloadFunction = (
  fileData: any,
  files: Array<FileResponse>,
  filesTableAction: (data: FilesTableActionInput) => void | null,
) => {
  if (fileData.type === FILE_TYPE.FOLDER) {
    // using the path of the folder we want to find all FILE_METADATA objects nested inside this folder
    const formattedFileDataInFolder = getFilesInFolder(fileData, files)
      // map them into objects containing key, path, and identityID which the API will use to create a zip file
      .map((f) => ({
        key: f.fields.fileKey,
        name: `${
          f.fields.fileName || getFileNameFromFileKey(f.fields.fileKey)
        }${getExtensionFromFileKey(f.fields.fileKey)}`,
        path: f.fields.path
          ? f.fields.path
              .split('/')
              .slice(fileData.filePath.length) // slice path of the file based on path depth of the targeted folder for download
              .join('/') // if path is present on the file object, create an altered path param omitting the first (parent) directory which API will already have
          : '',
        identityID: f.identityId,
      }));
    // return a func that will call the files table action with parameters that will trigger the .zip file download flow
    return formattedFileDataInFolder.length > 0
      ? () =>
          filesTableAction({
            actionType: FILES_ACTION.DOWNLOAD,
            fileData,
            fileBundleData: formattedFileDataInFolder,
            bundleName: fileData.fileName,
          })
      : null;
  }
  return !fileData.fileUrl
    ? () => filesTableAction({ actionType: FILES_ACTION.DOWNLOAD, fileData })
    : null;
};
export const FilesActionsRenderer: React.FC<FilesActionsRendererProps> = ({
  isClient,
  fileSettings,
  data,
  removeFile,
  renameFile,
  copyFile,
  copyLink,
  filesTableAction,
  onActionMenuClick,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const allFiles = useSelector((state: RootState) => state.files.items);
  const fileType = data.type;
  const { EnableFolderLocking } = React.useContext(FlagsContext);
  const [showConfirmationDialog, setShowConfirmationDialog] =
    React.useState(false);

  const downloadFunction = getDownloadFunction(
    data,
    allFiles,
    filesTableAction,
  );

  const downloadFile = React.useCallback(downloadFunction || (() => null), [
    allFiles,
  ]);
  const isEmptyFolder =
    fileType === FILE_TYPE.FOLDER &&
    getFilesInFolder(data, allFiles).length === 0;

  const folderHasSubfolders = React.useMemo(() => {
    if (fileType !== FILE_TYPE.FOLDER) return false;
    const folderPath = data.filePath.join('/');
    return FileUtils.hasSubfolders(folderPath, allFiles);
  }, [fileType, data.filePath, allFiles]);

  const previewFileActionLabel = data.fileUrl
    ? FILES_ACTION.VIEW
    : FILES_ACTION.PREVIEW;

  const previewFile =
    fileType !== FILE_TYPE.FOLDER
      ? () =>
          filesTableAction({
            actionType: previewFileActionLabel,
            fileData: data,
            fileLinkURLs: data.fileUrl ? [data.fileUrl] : undefined,
          })
      : null;

  const isDownloadAvailable = downloadFunction !== null;

  const fileOrLinkSuffix = isDownloadAvailable ? 'file' : 'link';
  const itemLabel = data.type === FILE_TYPE.FILE ? fileOrLinkSuffix : 'folder';
  const viewLabel = isDownloadAvailable ? `Preview ${itemLabel}` : 'Open link';
  const previewFileAction = previewFile
    ? [
        {
          name: viewLabel,
          icon: <PreviewIcon />,
          onClick: previewFile,
        },
      ]
    : [];

  const downloadFileAction =
    isDownloadAvailable || itemLabel === 'folder'
      ? [
          {
            name: `Download`,
            icon: <DownloadIcon />,
            onClick: downloadFile,
            isDisabled: isEmptyFolder,
            tooltipLabel: isEmptyFolder
              ? 'Folder with no contents cannot be downloaded.'
              : '',
          },
        ]
      : [];

  const handleRemoveFileClicked = () => {
    removeFile(data);
  };

  const handleRenameFileClicked = () => {
    renameFile(data);
  };

  const handleCopyFileClicked = () => {
    copyFile(data);
  };

  const handleCopyLinkClicked = () => {
    copyLink(data);
  };

  const deleteSettingDisabled = React.useMemo(() => {
    // base state is that delete is disabled if file delete is disabled
    let isDeleteDisabled = Boolean(fileSettings?.clientFileDeleteDisabled);

    // this can be overridden if folder update disable is turned off and fileType is folder
    // ...but only if this folder is empty. To delete a non-empty folder the user needs file and folder update ability
    if (fileType === FILE_TYPE.FOLDER) {
      isDeleteDisabled =
        Boolean(fileSettings?.clientFolderUpdateDisabled) ||
        (!isEmptyFolder && Boolean(fileSettings?.clientFileDeleteDisabled));
    }

    return isDeleteDisabled;
  }, [fileSettings, isEmptyFolder, fileType]);

  const isReadOnly = FileUtils.isReadOnly(data);

  const isLockedForClient =
    EnableFolderLocking &&
    isClient &&
    (fileType === FILE_TYPE.FOLDER
      ? isReadOnly
      : data.parentClientPermissions === FILE_ACCESS_LEVEL.READ_ONLY);

  const handleTogglePermission = React.useCallback(() => {
    if (folderHasSubfolders) {
      setShowConfirmationDialog(true);
    } else {
      // No subfolders, so skip the confirmation dialog and directly apply permission change
      const newPermission = isReadOnly
        ? FILE_ACCESS_LEVEL.READ_WRITE
        : FILE_ACCESS_LEVEL.READ_ONLY;
      dispatch(updateFolderPermissions(data.fileId, newPermission));
    }
  }, [folderHasSubfolders, isReadOnly, data.fileId, dispatch]);

  const handleConfirmPermissionChange = React.useCallback(() => {
    const newPermission = isReadOnly
      ? FILE_ACCESS_LEVEL.READ_WRITE
      : FILE_ACCESS_LEVEL.READ_ONLY;

    dispatch(updateFolderPermissions(data.fileId, newPermission));
    setShowConfirmationDialog(false);
  }, [data.fileId, dispatch, isReadOnly]);

  const togglePermissionAction = React.useMemo(() => {
    if (isClient || fileType !== FILE_TYPE.FOLDER || !EnableFolderLocking) {
      return [];
    }

    const permissionActionLabel = isReadOnly
      ? t(($) => $.messages.file_permission_change.unlock_access_label)
      : t(($) => $.messages.file_permission_change.lock_access_label);
    const permissionActionIcon = isReadOnly ? 'Unlock' : 'Authentication';

    return [
      {
        name: permissionActionLabel,
        onClick: handleTogglePermission,
        icon: <Icon icon={permissionActionIcon} />,
      },
    ];
  }, [
    fileType,
    EnableFolderLocking,
    isClient,
    isReadOnly,
    handleTogglePermission,
    t,
  ]);

  const actions = [
    ...downloadFileAction,
    ...previewFileAction,
    !isClient &&
      fileType === FILE_TYPE.FOLDER && {
        name: `Copy ${itemLabel} link`,
        icon: <Icon icon="Link" />,
        onClick: handleCopyLinkClicked,
        tooltipLabel: FILE_ACTION_DISABLE_TOOLTIP_LABEL,
      },
    ...togglePermissionAction,
    !isLockedForClient && {
      name: `Rename ${itemLabel}`,
      onClick: handleRenameFileClicked,
      icon: <Icon icon="Edit" />,
      isDisabled:
        isClient &&
        fileType === FILE_TYPE.FOLDER &&
        Boolean(fileSettings?.clientFolderUpdateDisabled),
      tooltipLabel: FILE_ACTION_DISABLE_TOOLTIP_LABEL,
    },
    !isClient && {
      name: `Duplicate ${itemLabel}`,
      icon: <Icon icon="Duplicate" />,
      onClick: handleCopyFileClicked,
      isDisabled: itemLabel === 'link',
      tooltipLabel: FILE_ACTION_DISABLE_TOOLTIP_LABEL,
    },
    !isLockedForClient && {
      name: `Delete ${itemLabel}`,
      onClick: handleRemoveFileClicked,
      icon: <Icon icon="Trash" />,
      isDelete: true,
      isDisabled: isClient && deleteSettingDisabled,
      tooltipLabel: FILE_ACTION_DISABLE_TOOLTIP_LABEL,
    },
  ];

  return (
    <>
      <BaseActionsMenuButton
        onActionMenuClick={onActionMenuClick}
        actions={compact(actions)}
      />
      {showConfirmationDialog && (
        <FileClientPermissionChangeConfirmationDialog
          open={showConfirmationDialog}
          onClose={() => setShowConfirmationDialog(false)}
          onConfirm={handleConfirmPermissionChange}
          isLocking={!isReadOnly}
          folderName={data.fileName}
        />
      )}
    </>
  );
};

export default FilesActionsRenderer;
